/* ============================================================
   Name Decal Upsell — pre-purchase offer modal.
   Paired files: snippets/js-upsell-name-decal.liquid,
   assets/js-upsell-name-decal.css.

   Golden rule: fail open. Nothing in here may ever block
   add-to-cart or checkout — every trigger path catches its own
   errors and falls through to the native action.
   ============================================================ */
(function () {
  'use strict';

  var config = null;
  var els = {};

  var state = {
    phase: 'idle', // idle | intercepted | open | busy | done
    mode: null, // 'atc' | 'checkout'
    resumeEl: null,
    bypassNextCheckoutClick: false,
    resolution: null, // 'accepted' | 'declined'
    cart: null,
    armedUntil: 0,
    drawerWasOpen: false,
    products: {}, // handle -> Promise<product JSON>
    selection: { fontKey: null, variantId: null },
    countdownTimer: null,
    countdownEndsAt: 0,
    ocuWatch: null,
    lockWatchdog: null,
    prevFocus: null,
    navigatingAway: false,
    memorySession: {} // sessionStorage fallback (private mode)
  };

  var ATC_ARM_SELECTOR = 'product-form button, [data-combo-add], [data-buy-two-button]';
  var CHECKOUT_SELECTOR = '#unicorn_checkout_submit, #unicorn_checkout_submit_alternative, button[name="checkout"]';
  var OFFER_LOAD_TIMEOUT_MS = 2500;
  var ARM_WINDOW_MS = 8000;

  /* ---------------------------------------------------------- */
  /* Bootstrap                                                  */
  /* ---------------------------------------------------------- */

  function init() {
    var configEl = document.querySelector('[data-ndu-config]');
    if (!configEl) return;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (err) {
      return;
    }
    if (!config || !config.enabled) return; // kill switch: nothing binds

    els.modal = document.getElementById('ndu-modal');
    if (!els.modal) return;
    els.form = els.modal.querySelector('[data-ndu-form]');
    els.previewImg = els.modal.querySelector('[data-ndu-preview-img]');
    els.zoomBtn = els.modal.querySelector('[data-ndu-zoom]');
    els.title = els.modal.querySelector('[data-ndu-title]');
    els.description = els.modal.querySelector('[data-ndu-description]');
    els.rowPrice = els.modal.querySelector('[data-ndu-row-price]');
    els.rowCompare = els.modal.querySelector('[data-ndu-row-compare]');
    els.rowBadge = els.modal.querySelector('[data-ndu-row-badge]');
    els.fontsWrap = els.modal.querySelector('[data-ndu-fonts]');
    els.fontsList = els.modal.querySelector('[data-ndu-fonts-list]');
    els.swatchList = els.modal.querySelector('[data-ndu-swatches-list]');
    els.colorName = els.modal.querySelector('[data-ndu-color-name]');
    els.nameInput = els.modal.querySelector('[data-ndu-name]');
    els.nameError = els.modal.querySelector('#ndu-name-error');
    els.accept = els.modal.querySelector('[data-ndu-accept]');
    els.acceptPrice = els.modal.querySelector('[data-ndu-price]');
    els.acceptCompare = els.modal.querySelector('[data-ndu-compare]');
    els.acceptBadge = els.modal.querySelector('[data-ndu-badge]');
    els.submitError = els.modal.querySelector('[data-ndu-submit-error]');
    els.decline = els.modal.querySelector('[data-ndu-decline]');
    els.countdown = els.modal.querySelector('[data-ndu-countdown]');

    document.addEventListener('theme:cart:change', onCartChange);
    document.addEventListener('theme:cart:popdown', onPopdown);
    document.addEventListener('click', onDocClickCapture, true);
    window.addEventListener('beforeunload', function () { state.navigatingAway = true; });
    window.addEventListener('pagehide', function () { state.navigatingAway = true; });

    els.form.addEventListener('submit', onAcceptSubmit);
    els.decline.addEventListener('click', function () {
      closeModal('declined');
    });
    els.nameInput.addEventListener('input', clearNameError);
    els.fontsList.addEventListener('change', onFontChange);
    els.swatchList.addEventListener('change', onSwatchChange);
    els.zoomLabel = els.modal.querySelector('[data-ndu-zoom-label]');
    els.zoomHint = els.modal.querySelector('.ndu-preview__zoom-hint');
    if (els.zoomBtn) {
      els.zoomBtn.addEventListener('click', function () {
        var zoomed = els.zoomBtn.classList.toggle('is-zoomed');
        els.zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        setZoomLabel(zoomed);
      });
    }

    prewarm();
  }

  function prewarm() {
    var idle = window.requestIdleCallback || function (cb) { setTimeout(cb, 3000); };
    idle(function () {
      loadOffers().catch(function () {});
      loadSwatchColors();
    }, { timeout: 4000 });
  }

  /* ---------------------------------------------------------- */
  /* Session gate                                               */
  /* ---------------------------------------------------------- */

  function readSession() {
    try {
      var raw = sessionStorage.getItem(config.sessionKey);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      return state.memorySession;
    }
  }

  function writeSession(patch) {
    var current = readSession();
    for (var k in patch) current[k] = patch[k];
    try {
      sessionStorage.setItem(config.sessionKey, JSON.stringify(current));
    } catch (err) {
      state.memorySession = current;
    }
  }

  // Only an explicit decline (the link inside the modal) retires the offer
  // for the session. Esc / backdrop / the close X merely dismiss it — the
  // offer shows again on the next trigger.
  function alreadyOffered() {
    if (config.frequency !== 'once_per_session') return false;
    return readSession().outcome === 'declined';
  }

  /* ---------------------------------------------------------- */
  /* Product data                                               */
  /* ---------------------------------------------------------- */

  function fetchProduct(handle) {
    if (!state.products[handle]) {
      state.products[handle] = fetch('/products/' + handle + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('product fetch failed: ' + res.status);
          return res.json();
        })
        .catch(function (err) {
          delete state.products[handle]; // allow retry on a later open
          throw err;
        });
    }
    return state.products[handle];
  }

  function loadOffers() {
    return Promise.all(
      config.offers.map(function (offer) {
        return fetchProduct(offer.handle).then(function (product) {
          return { offer: offer, product: product };
        });
      })
    );
  }

  // Colour-name -> hex/image map from the theme's swatches.json — the same
  // source the PDP swatches use. Never rejects: null means "fall back to
  // variant image thumbs".
  var swatchColorsPromise = null;
  function loadSwatchColors() {
    if (!swatchColorsPromise) {
      swatchColorsPromise = Promise.resolve()
        .then(function () {
          var url = window.theme && window.theme.assets && window.theme.assets.swatches;
          if (!url) throw new Error('no swatch json');
          return fetch(url, { headers: { Accept: 'application/json' } });
        })
        .then(function (res) {
          if (!res.ok) throw new Error('swatch fetch failed');
          return res.json();
        })
        .then(function (data) {
          var map = {};
          (data.colors || []).forEach(function (entry) {
            var key = Object.keys(entry)[0];
            if (key) map[key.toLowerCase().replace(/\s/g, '')] = entry[key];
          });
          return map;
        })
        .catch(function () {
          return null;
        });
    }
    return swatchColorsPromise;
  }

  /* ---------------------------------------------------------- */
  /* Cart awareness                                             */
  /* ---------------------------------------------------------- */

  function onCartChange(e) {
    try {
      var next = e.detail && e.detail.cart;
      if (!next || !next.items) return;
      var prev = state.cart;
      state.cart = next;
      if (state.armedUntil > Date.now() && state.phase === 'idle') {
        state.armedUntil = 0;
        var added = diffCarts(prev, next);
        if (added) maybeTriggerAtc(added.product_id, added.handle);
      }
    } catch (err) {
      /* fail open */
    }
  }

  // Returns the line that is new or whose quantity increased, else null.
  function diffCarts(prev, next) {
    if (!prev || !prev.items) return null;
    var prevByKey = {};
    prev.items.forEach(function (item) {
      prevByKey[item.key] = item.quantity;
    });
    for (var i = 0; i < next.items.length; i++) {
      var item = next.items[i];
      var was = prevByKey[item.key];
      if (was === undefined || item.quantity > was) return item;
    }
    return null;
  }

  function ensureCart() {
    if (state.cart) return Promise.resolve(state.cart);
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart fetch failed');
        return res.json();
      })
      .then(function (cart) {
        state.cart = cart;
        return cart;
      });
  }

  function fontKeysInCart(cart) {
    var inCart = {};
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      config.offers.forEach(function (offer) {
        if (item.product_id === offer.productId) inCart[offer.key] = true;
      });
    });
    return inCart;
  }

  function isOfferProduct(productId, handle) {
    return config.offers.some(function (offer) {
      return offer.productId === productId || offer.handle === handle;
    });
  }

  /* ---------------------------------------------------------- */
  /* Trigger A — after add to cart                              */
  /* ---------------------------------------------------------- */

  function onPopdown(e) {
    try {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      maybeTriggerAtc(variant.product_id, variant.handle);
    } catch (err) {
      /* fail open */
    }
  }

  function maybeTriggerAtc(productId, handle) {
    try {
      if (!config.triggers.afterAddToCart) return;
      if (state.phase !== 'idle') return;
      if (alreadyOffered()) return;
      // Never offer a name decal to someone who just added one.
      if (isOfferProduct(productId, handle)) return;
      if (!config.triggers.allProducts) {
        if (!handle || config.triggers.productHandles.indexOf(handle) === -1) return;
      }
      openModal({ mode: 'atc' });
    } catch (err) {
      /* fail open */
    }
  }

  /* ---------------------------------------------------------- */
  /* Trigger B — checkout interception (+ Trigger A arming)     */
  /* ---------------------------------------------------------- */

  function onDocClickCapture(e) {
    // (a) Arm the add-to-cart fallback. Zero-risk: only sets a timestamp.
    try {
      if (e.target.closest && e.target.closest(ATC_ARM_SELECTOR)) {
        state.armedUntil = Date.now() + ARM_WINDOW_MS;
      }
    } catch (err) {
      /* ignore */
    }

    // (b) Checkout interception. All gates run BEFORE preventDefault so
    // any error means the click proceeds natively (fail open).
    var btn;
    try {
      btn = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
      if (!btn) return;
      if (state.bypassNextCheckoutClick) {
        state.bypassNextCheckoutClick = false;
        return;
      }
      if (!config.triggers.beforeCheckout) return;
      if (state.phase !== 'idle') return;
      if (alreadyOffered()) return;
      // Quick pre-check from the latest snapshot; openModal re-checks with
      // fresh data and resumes checkout if nothing is offerable.
      if (state.cart) {
        var inCart = fontKeysInCart(state.cart);
        if (config.triggers.skipIfDecalInCart && Object.keys(inCart).length) return;
        var anyAvailable = config.offers.some(function (offer) {
          return !inCart[offer.key];
        });
        if (!anyAvailable) return;
      }
    } catch (err) {
      return; // never intercept on error
    }

    e.preventDefault();
    e.stopPropagation();
    state.resumeEl = btn;
    openModal({ mode: 'checkout' });
  }

  function resumeCheckout() {
    try {
      state.bypassNextCheckoutClick = true;
      var el = state.resumeEl;
      if (el && el.isConnected) {
        // Synthetic click re-enters the Unicorn/theme handler so terms
        // validation and discount attributes are preserved.
        el.click();
        scheduleResumeFallback(el, 2000);
      } else {
        window.location.href = '/checkout';
      }
    } catch (err) {
      window.location.href = '/checkout';
    }
  }

  // Verified on this store: a third-party script stops synthetic clicks on
  // checkout buttons at document capture (isTrusted filtering), so the
  // re-click can be silently eaten. If checkout hasn't started shortly
  // after, navigate directly — never leave the customer stranded.
  function scheduleResumeFallback(el, delay) {
    setTimeout(function () {
      if (state.navigatingAway) return;
      state.bypassNextCheckoutClick = false;
      var handlerBusy = false;
      try {
        handlerBusy = String(el.className || '').indexOf('loading') !== -1;
      } catch (err) {
        /* ignore */
      }
      if (handlerBusy) {
        // The click DID land and the app is applying discounts — give it
        // a grace period before forcing navigation.
        setTimeout(function () {
          if (!state.navigatingAway) window.location.href = '/checkout';
        }, 4000);
        return;
      }
      window.location.href = '/checkout';
    }, delay);
  }

  /* ---------------------------------------------------------- */
  /* Modal lifecycle                                            */
  /* ---------------------------------------------------------- */

  function getMicroModal() {
    return window.MicroModal || (window.themeVendor && window.themeVendor.MicroModal) || null;
  }

  function unicornDialogOpen() {
    var dialog = document.getElementById('unicorn-cart-dialog');
    return !!(dialog && dialog.open);
  }

  function closeUnicornDrawer() {
    try {
      if (window.Unicorn && typeof window.Unicorn.closeCart === 'function') {
        window.Unicorn.closeCart();
      }
      var dialog = document.getElementById('unicorn-cart-dialog');
      if (dialog && dialog.open && typeof dialog.close === 'function') dialog.close();
    } catch (err) {
      /* ignore */
    }
  }

  // Unicorn auto-opens its top-layer <dialog> in reaction to the cart change
  // that precedes our modal — and the top layer paints OVER the modal. Keep
  // the drawer closed for as long as the offer is open, then let finish()
  // reopen it on decline.
  function startDrawerGuard() {
    stopDrawerGuard();
    var dialog = document.getElementById('unicorn-cart-dialog');
    if (!dialog || typeof MutationObserver === 'undefined') return;
    state.drawerGuard = new MutationObserver(function () {
      if (state.phase !== 'open' && state.phase !== 'busy') {
        stopDrawerGuard();
        return;
      }
      if (dialog.open) {
        state.drawerWasOpen = true; // decline restores it
        closeUnicornDrawer();
      }
    });
    state.drawerGuard.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  function stopDrawerGuard() {
    if (state.drawerGuard) {
      try {
        state.drawerGuard.disconnect();
      } catch (err) {
        /* ignore */
      }
      state.drawerGuard = null;
    }
  }

  // If OCU's popup arrives AFTER ours opened (lost the race despite the
  // grace window), step aside: close ours as a dismissal (which re-arms),
  // let OCU run, and reopen once it's done. Never yank the modal while
  // the customer is typing their name.
  function startOcuWatch() {
    stopOcuWatch();
    var checks = 0;
    state.ocuWatch = setInterval(function () {
      checks++;
      if (state.phase !== 'open' || checks > 20) {
        stopOcuWatch();
        return;
      }
      if (!ocuPopupVisible()) return;
      if (els.nameInput.value) {
        stopOcuWatch();
        return;
      }
      stopOcuWatch();
      closeModal('dismissed');
      waitForOcuClear().then(function (cleared) {
        if (cleared && state.phase === 'idle') openModal({ mode: 'atc' });
      });
    }, 200);
  }

  function stopOcuWatch() {
    if (state.ocuWatch) {
      clearInterval(state.ocuWatch);
      state.ocuWatch = null;
    }
  }

  // iOS freeze guard. The theme's scroll lock (theme:scroll:lock) blocks
  // ALL touchmove except TEXTAREA — no whitelist for our modal. If the
  // theme cart drawer opens behind the modal (the live theme.js has a
  // custom snippet that force-opens it after add-to-cart) or any other
  // component engages the lock while the offer is open, the modal becomes
  // untouchable on mobile Safari. Keep drawers closed and the lock
  // released for as long as the modal is open; MicroModal's own body
  // overflow lock still holds the page behind.
  function startLockWatchdog() {
    stopLockWatchdog();
    state.lockWatchdog = setInterval(function () {
      if (state.phase !== 'open' && state.phase !== 'busy') {
        stopLockWatchdog();
        return;
      }
      try {
        var drawers = document.querySelectorAll('[data-drawer].drawer--visible');
        for (var i = 0; i < drawers.length; i++) {
          drawers[i].dispatchEvent(new CustomEvent('theme:drawer:close', { bubbles: false }));
        }
        if (document.documentElement.hasAttribute('data-scroll-locked')) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
        }
      } catch (err) {
        /* never let the watchdog break the modal */
      }
    }, 300);
  }

  function stopLockWatchdog() {
    if (state.lockWatchdog) {
      clearInterval(state.lockWatchdog);
      state.lockWatchdog = null;
    }
  }

  // iOS-safe background lock. MicroModal's overflow:hidden body lock does
  // not stop touch scrolling on iOS Safari (and the watchdog above keeps
  // the theme's touch-blocking lock released), so pin the body in place
  // while the modal is open and restore the scroll position on close.
  function setZoomLabel(zoomed) {
    if (!els.zoomLabel || !els.zoomHint) return;
    var text = zoomed
      ? els.zoomHint.getAttribute('data-zoom-out-label') || 'Zoom out'
      : els.zoomHint.getAttribute('data-zoom-in-label') || 'Zoom in';
    els.zoomLabel.textContent = text;
  }

  function lockBody() {
    try {
      state.savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = '-' + state.savedScrollY + 'px';
      document.body.classList.add('ndu-body-locked');
    } catch (err) {
      /* ignore */
    }
  }

  function unlockBody() {
    try {
      document.body.classList.remove('ndu-body-locked');
      document.body.style.top = '';
      if (typeof window.scrollTo === 'function') {
        window.scrollTo(0, state.savedScrollY || 0);
      }
    } catch (err) {
      /* ignore */
    }
  }

  function failOpen(ctx) {
    state.phase = 'idle';
    if (ctx.mode === 'checkout') resumeCheckout();
  }

  // Zipify OCU coexistence: if OCU's own popup is on screen, let it run
  // first and show our offer after it closes. Prefix-anchored selectors so
  // e.g. ids containing "focus" don't false-match "ocu".
  var OCU_SELECTOR = '#ocuapp, [id^="ocu-"], [id^="OCU"], [class^="ocu-"], [class*=" ocu-"], [id^="zipify"], [class*="zipify"], iframe[src*="zipify"], iframe[src*="oneclickupsell"]';

  function ocuPopupVisible() {
    try {
      var candidates = document.querySelectorAll(OCU_SELECTOR);
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (typeof el.getBoundingClientRect !== 'function') return true;
        var rect = el.getBoundingClientRect();
        if (rect.width > 200 && rect.height > 150) return true;
      }
    } catch (err) {
      /* fail open: treat as not visible */
    }
    return false;
  }

  // Is Zipify OCU even installed on this page? If not, no grace delay.
  function ocuPresent() {
    try {
      return !!(window.OCUApi || document.querySelector('[data-ocu-checkout], script[src*="zipify"], script[src*="oneclickupsell"]'));
    } catch (err) {
      return false;
    }
  }

  // OCU gets first go. Wait up to graceMs for its popup to appear; if it
  // appears (or already has), hold until it closes. Resolves like
  // waitForOcuClear: false = OCU never showed, true = showed then closed,
  // null = never closed (fail open).
  function waitForOcuTurn(graceMs) {
    if (!graceMs) return waitForOcuClear();
    return new Promise(function (resolve) {
      var started = Date.now();
      var check = function () {
        if (ocuPopupVisible()) {
          waitForOcuClear().then(resolve);
          return;
        }
        if (Date.now() - started >= graceMs) {
          resolve(false);
          return;
        }
        setTimeout(check, 200);
      };
      check();
    });
  }

  // Resolves false (OCU never visible), true (was visible, now closed),
  // or null (still visible after the timeout — give up, fail open).
  function waitForOcuClear() {
    if (!ocuPopupVisible()) return Promise.resolve(false);
    return new Promise(function (resolve) {
      var waited = 0;
      var poll = setInterval(function () {
        waited += 400;
        if (!ocuPopupVisible()) {
          clearInterval(poll);
          resolve(true);
        } else if (waited >= 120000) {
          clearInterval(poll);
          resolve(null);
        }
      }, 400);
    });
  }

  function openModal(ctx) {
    state.phase = 'intercepted';
    state.mode = ctx.mode;
    state.resolution = null;

    var timeout = new Promise(function (_, reject) {
      setTimeout(function () {
        reject(new Error('ndu: offer load timed out'));
      }, OFFER_LOAD_TIMEOUT_MS);
    });

    Promise.race([Promise.all([loadOffers(), ensureCart(), loadSwatchColors()]), timeout])
      .then(function (results) {
        state.swatchColors = results[2];
        // Post-ATC, OCU's own popup may be about to appear — give it a
        // head start so ours never blocks it. No delay when OCU is absent
        // or the customer is mid-checkout.
        var grace = ctx.mode === 'atc' && ocuPresent() ? 1500 : 0;
        return waitForOcuTurn(grace).then(function (ocuWaited) {
          if (ocuWaited === null) return null; // OCU never closed — fail open
          if (!ocuWaited) return { offers: results[0], cart: results[1] };
          // OCU's popup ran first and may have changed the cart — re-check.
          state.cart = null;
          return ensureCart().then(function (freshCart) {
            return { offers: results[0], cart: freshCart };
          });
        });
      })
      .then(function (payload) {
        if (!payload) return failOpen(ctx);
        var offers = payload.offers;
        var cart = payload.cart;

        var MicroModal = getMicroModal();
        if (!MicroModal) return failOpen(ctx);

        var inCart = fontKeysInCart(cart);
        // Merchant rule: don't pitch a name decal to a cart that already
        // holds one (any font). Configurable via skipIfDecalInCart.
        if (config.triggers.skipIfDecalInCart && Object.keys(inCart).length) {
          return failOpen(ctx);
        }
        var available = offers.filter(function (entry) {
          return !inCart[entry.offer.key];
        });
        if (!available.length) return failOpen(ctx);

        // The Unicorn drawer is a top-layer <dialog>; anything else
        // renders behind it. Close it before showing the offer.
        state.drawerWasOpen = unicornDialogOpen();
        if (state.drawerWasOpen) closeUnicornDrawer();

        state.available = available;
        renderFonts(available);
        selectFont(available[0].offer.key);
        resetNameError();
        hideSubmitError();
        setAcceptBusy(false);

        if (els.zoomBtn) {
          els.zoomBtn.classList.remove('is-zoomed');
          els.zoomBtn.setAttribute('aria-pressed', 'false');
          setZoomLabel(false);
        }

        state.prevFocus = document.activeElement;
        document.body.classList.add('ndu-modal-open');
        lockBody();
        state.phase = 'open';

        MicroModal.show('ndu-modal', {
          disableScroll: true,
          awaitCloseAnimation: false,
          onClose: onModalClose
        });
        startDrawerGuard();
        startLockWatchdog();
        if (ctx.mode === 'atc') startOcuWatch();

        if (config.countdown.enabled) startCountdown();
      })
      .catch(function () {
        failOpen(ctx);
      });
  }

  // Programmatic close with an explicit outcome; MicroModal onClose
  // does the shared teardown.
  function closeModal(resolution) {
    state.resolution = resolution;
    var MicroModal = getMicroModal();
    try {
      MicroModal.close('ndu-modal');
    } catch (err) {
      onModalClose();
    }
  }

  // Runs for every close. Esc / backdrop / close X count as 'dismissed'
  // (offer stays eligible); only the decline link and the countdown expiry
  // pass 'declined' and retire the offer for the session.
  function onModalClose() {
    if (state.phase !== 'open' && state.phase !== 'busy') return;
    if (!state.resolution) state.resolution = 'dismissed';
    document.body.classList.remove('ndu-modal-open');
    unlockBody();
    stopDrawerGuard();
    stopOcuWatch();
    stopLockWatchdog();
    stopCountdown();
    if (state.resolution !== 'dismissed') {
      writeSession({ outcome: state.resolution, ts: Date.now() });
    }
    var wasCheckout = state.mode === 'checkout';
    finish();
    // A dismissal re-arms the trigger on this very page; decline retires
    // it. Checkout mode is navigating away regardless.
    state.phase = state.resolution === 'declined' || wasCheckout ? 'done' : 'idle';
  }

  function finish() {
    if (state.mode === 'checkout') {
      resumeCheckout();
      return;
    }
    // Trigger A
    if (state.resolution !== 'accepted' && state.drawerWasOpen) {
      try {
        if (window.Unicorn && typeof window.Unicorn.openCart === 'function') {
          window.Unicorn.openCart();
        }
      } catch (err) {
        /* non-destructive: item is in cart either way */
      }
    }
    try {
      if (state.prevFocus && state.prevFocus.isConnected) state.prevFocus.focus();
    } catch (err) {
      /* ignore */
    }
  }

  /* ---------------------------------------------------------- */
  /* Rendering                                                  */
  /* ---------------------------------------------------------- */

  function findEntry(fontKey) {
    for (var i = 0; i < state.available.length; i++) {
      if (state.available[i].offer.key === fontKey) return state.available[i];
    }
    return null;
  }

  function selectedVariant() {
    var entry = findEntry(state.selection.fontKey);
    if (!entry) return null;
    for (var i = 0; i < entry.product.variants.length; i++) {
      if (entry.product.variants[i].id === state.selection.variantId) {
        return entry.product.variants[i];
      }
    }
    return null;
  }

  function renderFonts(available) {
    els.fontsList.innerHTML = '';
    available.forEach(function (entry) {
      var label = document.createElement('label');
      label.className = 'ndu-pill';
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'ndu-font';
      input.value = entry.offer.key;
      var span = document.createElement('span');
      span.textContent = entry.offer.label;
      label.appendChild(input);
      label.appendChild(span);
      els.fontsList.appendChild(label);
    });
    // Single remaining font: keep the control visible (customer should
    // still see what they're getting) but there is nothing to switch.
  }

  function onFontChange(e) {
    if (e.target && e.target.name === 'ndu-font') selectFont(e.target.value);
  }

  function selectFont(fontKey) {
    var entry = findEntry(fontKey);
    if (!entry) return;
    state.selection.fontKey = fontKey;

    var input = els.fontsList.querySelector('input[value="' + fontKey + '"]');
    if (input) input.checked = true;

    els.title.textContent = entry.product.title;
    els.description.textContent = stripHtml(entry.product.description || '');

    renderSwatches(entry.product);

    // First available variant; typed name is preserved automatically
    // because the input is never re-rendered.
    var first = null;
    for (var i = 0; i < entry.product.variants.length; i++) {
      if (entry.product.variants[i].available) {
        first = entry.product.variants[i];
        break;
      }
    }
    if (first) selectVariant(first.id);
  }

  function renderSwatches(product) {
    els.swatchList.innerHTML = '';
    product.variants.forEach(function (variant) {
      if (!variant.available) return;
      var label = document.createElement('label');
      label.className = 'ndu-swatch';
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'ndu-color';
      input.value = String(variant.id);
      var thumb = document.createElement('span');
      thumb.className = 'ndu-swatch__thumb';
      applySwatchStyle(thumb, variant);
      var srText = document.createElement('span');
      srText.className = 'visually-hidden';
      // Always positional: the Bold product's option is named "color"
      // (lowercase) — never look options up by name.
      srText.textContent = variant.option1;
      label.appendChild(input);
      label.appendChild(thumb);
      label.appendChild(srText);
      els.swatchList.appendChild(label);
    });
  }

  // Flat colour chips like the PDP: hex or swatch-image from swatches.json,
  // matched on the variant's colour name. The variant's featured_image is
  // only used for the MAIN preview (and as a thumb fallback).
  function applySwatchStyle(thumb, variant) {
    var value = null;
    if (state.swatchColors) {
      var key = String(variant.option1 || '').toLowerCase().replace(/\s/g, '');
      value = state.swatchColors[key] || null;
    }
    if (value && /\.(jpe?g|png|svg)(\?|$)/i.test(value)) {
      var base = (window.theme && window.theme.assets && window.theme.assets.base) || '/';
      thumb.style.backgroundImage = 'url(' + base + encodeURIComponent(value) + ')';
    } else if (value) {
      thumb.style.backgroundColor = value;
    } else if (variant.featured_image && variant.featured_image.src) {
      thumb.style.backgroundImage = 'url(' + imageUrl(variant.featured_image.src, 100) + ')';
    } else {
      thumb.style.backgroundColor = '#E5E5E5';
    }
  }

  function onSwatchChange(e) {
    if (e.target && e.target.name === 'ndu-color') {
      selectVariant(parseInt(e.target.value, 10));
    }
  }

  function selectVariant(variantId) {
    state.selection.variantId = variantId;
    var input = els.swatchList.querySelector('input[value="' + variantId + '"]');
    if (input) input.checked = true;

    var variant = selectedVariant();
    if (!variant) return;

    els.colorName.textContent = '│ ' + variant.option1;
    if (variant.featured_image && variant.featured_image.src) {
      els.previewImg.src = imageUrl(variant.featured_image.src, 900);
      els.previewImg.alt = els.title.textContent + ' — ' + variant.option1;
    }
    renderPrice(variant);
  }

  function renderPrice(variant) {
    var price = variant.price;
    var compare = variant.compare_at_price;
    var hasCompare = typeof compare === 'number' && compare > price;

    els.rowPrice.textContent = money(price);
    els.acceptPrice.textContent = money(price);

    [
      [els.rowCompare, els.rowBadge],
      [els.acceptCompare, els.acceptBadge]
    ].forEach(function (pair) {
      pair[0].hidden = !hasCompare;
      pair[1].hidden = !hasCompare;
      if (hasCompare) {
        pair[0].textContent = money(compare);
        // "Save $20" not "Save $20.00" — matches the PDP's
        // money_without_trailing_zeros badge format.
        pair[1].textContent = config.copy.saveBadge + ' ' + moneyCompact(compare - price);
      }
    });
  }

  function money(cents) {
    try {
      // The formatter lives at themeVendor.themeCurrency on this theme
      // (window.themeCurrency does NOT exist on the storefront). Resolving
      // both keeps market money formats working — e.g. JPY ¥3,500.
      var tc = window.themeCurrency || (window.themeVendor && window.themeVendor.themeCurrency);
      var formatted = tc.formatMoney(cents, window.theme.moneyFormat);
      if (window.theme.settings && window.theme.settings.currency_code_enable) {
        formatted += ' ' + window.theme.currencyCode;
      }
      return formatted;
    } catch (err) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function moneyCompact(cents) {
    return money(cents).replace(/([.,])00(?!\d)/, '');
  }

  function imageUrl(src, width) {
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (err) {
      return src;
    }
  }

  function stripHtml(html) {
    var div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }

  /* ---------------------------------------------------------- */
  /* Validation + accept                                        */
  /* ---------------------------------------------------------- */

  // The name rule is NOT written here. It comes from
  // snippets/pnd-name-rules.liquid via config.nameRules, so this
  // popup and the product page form always enforce the same thing.
  // Change the rule in Theme settings -> "Personalised name decals".
  //
  // Fallback below is deliberately the strictest sensible rule: if
  // the config ever fails to render, we reject more than we should
  // rather than letting an uncuttable name through to production.
  var FALLBACK_RULES = {
    maxLength: 15,
    pattern: '^[A-Za-z]+$',
    flags: '',
    collapseSpaces: false,
    requiredMessage: 'Please enter a name to continue.',
    tooLongMessage: 'Names can be up to 15 letters.',
    invalidMessage: 'Please use letters only, without accents.'
  };

  function nameRules() {
    var r = config && config.nameRules;
    if (!r || !r.pattern) return FALLBACK_RULES;
    return r;
  }

  function validateName(raw) {
    var rules = nameRules();

    var name = (raw || '').trim();
    if (rules.collapseSpaces) name = name.replace(/\s+/g, ' ');

    if (!name) return { error: rules.requiredMessage };
    if (name.length > rules.maxLength) return { error: rules.tooLongMessage };

    var re;
    try {
      re = new RegExp(rules.pattern, rules.flags || '');
    } catch (err) {
      // Engine without unicode property escapes (older Safari) and the
      // merchant has accents switched on. Fall back to the same rule
      // without the accent support rather than accepting anything.
      try {
        re = new RegExp(rules.pattern.replace(/\\p\{[LM]\}/g, 'A-Za-z'), '');
      } catch (err2) {
        re = /^[A-Za-z]+$/;
      }
    }
    if (!re.test(name)) return { error: rules.invalidMessage };

    return { name: name }; // capitalisation preserved exactly
  }

  function showNameError(message) {
    els.nameError.textContent = message;
    els.nameError.hidden = false;
    els.nameInput.classList.add('error');
    els.nameInput.setAttribute('aria-invalid', 'true');
    els.nameInput.focus();
  }

  function clearNameError() {
    if (!els.nameError.hidden) resetNameError();
  }

  function resetNameError() {
    els.nameError.hidden = true;
    els.nameError.textContent = '';
    els.nameInput.classList.remove('error');
    els.nameInput.removeAttribute('aria-invalid');
  }

  function showSubmitError(message) {
    els.submitError.textContent = message;
    els.submitError.hidden = false;
  }

  function hideSubmitError() {
    els.submitError.hidden = true;
    els.submitError.textContent = '';
  }

  function setAcceptBusy(busy) {
    els.accept.disabled = busy;
    els.accept.classList.toggle('loading', busy);
  }

  // The single extension point for the cart payload. Future discount
  // logic (selling plans, signalling properties, quantities) goes here.
  function buildLineItem(name) {
    var properties = {};
    properties[config.propertyKey] = name; // key exactly "Sticker Name"
    return {
      id: Number(state.selection.variantId),
      quantity: 1,
      properties: properties
    };
  }

  function addToCart(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(
            function (data) {
              throw new Error(data.description || data.message || 'add failed');
            },
            function () {
              throw new Error('add failed');
            }
          );
        }
        return res.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (cart) {
          state.cart = cart;
          document.dispatchEvent(
            new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
          );
        }
        try {
          if (window.Unicorn && typeof window.Unicorn.refreshCart === 'function') {
            window.Unicorn.refreshCart();
          }
        } catch (err) {
          /* drawer refresh is cosmetic */
        }
      });
  }

  function onAcceptSubmit(e) {
    e.preventDefault();
    if (state.phase !== 'open') return; // double-click guard

    var result = validateName(els.nameInput.value);
    if (result.error) {
      showNameError(result.error);
      return;
    }
    if (!state.selection.variantId) return;

    resetNameError();
    hideSubmitError();
    setAcceptBusy(true);
    state.phase = 'busy';

    addToCart(buildLineItem(result.name))
      .then(function () {
        state.phase = 'open'; // so onModalClose runs its teardown
        closeModal('accepted');
      })
      .catch(function () {
        state.phase = 'open';
        setAcceptBusy(false);
        // Keep the modal open with an escape hatch: decline still
        // resumes checkout, so the customer is never trapped.
        showSubmitError(config.copy.errorAddFailed);
      });
  }

  /* ---------------------------------------------------------- */
  /* Countdown (config-gated, default off)                      */
  /* ---------------------------------------------------------- */

  function startCountdown() {
    state.countdownEndsAt = Date.now() + config.countdown.minutes * 60000;
    els.countdown.hidden = false;
    renderCountdown();
    state.countdownTimer = setInterval(renderCountdown, 1000);
  }

  function renderCountdown() {
    var remaining = Math.max(0, state.countdownEndsAt - Date.now());
    var mins = Math.floor(remaining / 60000);
    var secs = Math.floor((remaining % 60000) / 1000);
    els.countdown.textContent =
      config.copy.countdownLabel + ': ' + pad(mins) + ':' + pad(secs);
    if (remaining <= 0) {
      stopCountdown();
      // The offer genuinely closes when the timer hits zero.
      if (state.phase === 'open') closeModal('declined');
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function stopCountdown() {
    if (state.countdownTimer) {
      clearInterval(state.countdownTimer);
      state.countdownTimer = null;
    }
    els.countdown.hidden = true;
  }

  /* ---------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();